import { WorkOrderRecord } from '../types/workOrder';

import { createQueryAtomFactory } from '~/shared/providers/api/createQueryAtomFactory';

export const workOrderQueryAtom = createQueryAtomFactory(
  'production.workOrder',
  (params: { id: string }) => ({
    workOrder: {
      __args: {
        id: params.id,
      },
      id: true,
      workOrderNumber: true,
      status: true,
      processingTypeId: true,
      processingType: {
        id: true,
        name: true,
        code: true,
      },
      facilityId: true,
      facility: {
        id: true,
        facilityName: true,
      },
      scheduledStartDate: true,
      scheduledEndDate: true,
      actualStartDate: true,
      actualEndDate: true,
      notes: true,
      tollingType: true,
      outboundTollingAgreementId: true,
      inboundTollingAgreementId: true,
      sourcePurchaseLoadLineItemId: true,
      createdBy: true,
      createdByUser: {
        id: true,
        firstName: true,
        lastName: true,
      },
      inputItems: {
        id: true,
        itemId: true,
        item: {
          id: true,
          name: true,
        },
        lotId: true,
        lot: {
          id: true,
          lotNumber: true,
        },
        tagId: true,
        tag: {
          id: true,
          tagNumber: true,
        },
        plannedQuantity: true,
        plannedQuantityUnit: true,
        actualQuantity: true,
        actualQuantityUnit: true,
        notes: true,
      },
      outputItems: {
        id: true,
        itemId: true,
        item: {
          id: true,
          name: true,
        },
        targetLotId: true,
        targetLot: {
          id: true,
          lotNumber: true,
        },
        targetTagId: true,
        targetTag: {
          id: true,
          tagNumber: true,
        },
        expectedQuantity: true,
        expectedQuantityUnit: true,
        actualQuantity: true,
        actualQuantityUnit: true,
        yieldPercentage: true,
        notes: true,
      },
      timestamps: {
        createdAt: true,
        updatedAt: true,
      },
    },
  }),
  {
    select: (data): WorkOrderRecord | null => {
      const wo = data?.workOrder;
      if (!wo) return null;

      return {
        id: wo.id,
        workOrderNumber: wo.workOrderNumber,
        status: wo.status,
        processingTypeId: wo.processingTypeId,
        processingTypeName: wo.processingType?.name,
        processingTypeCode: wo.processingType?.code,
        facilityId: wo.facilityId,
        facilityName: wo.facility?.facilityName,
        scheduledStartDate: wo.scheduledStartDate ?? undefined,
        scheduledEndDate: wo.scheduledEndDate ?? undefined,
        actualStartDate: wo.actualStartDate ?? undefined,
        actualEndDate: wo.actualEndDate ?? undefined,
        notes: wo.notes ?? undefined,
        isTolling: wo.tollingType !== 'None',
        tollingType: wo.tollingType,
        outboundTollingAgreementId: wo.outboundTollingAgreementId ?? undefined,
        inboundTollingAgreementId: wo.inboundTollingAgreementId ?? undefined,
        sourcePurchaseLoadLineItemId:
          wo.sourcePurchaseLoadLineItemId ?? undefined,
        createdById: wo.createdBy ?? '',
        createdByName:
          wo.createdByUser?.firstName && wo.createdByUser?.lastName
            ? `${wo.createdByUser.firstName} ${wo.createdByUser.lastName}`
            : undefined,
        inputItems: (wo.inputItems ?? []).map((item) => ({
          id: item.id,
          itemId: item.itemId,
          itemName: item.item?.name,
          lotId: item.lotId ?? undefined,
          lotNumber: item.lot?.lotNumber,
          tagId: item.tagId ?? undefined,
          tagNumber: item.tag?.tagNumber,
          plannedQuantityValue: Number(item.plannedQuantity) || 0,
          plannedQuantityUnit: item.plannedQuantityUnit || 'Pounds',
          actualQuantityValue: item.actualQuantity
            ? Number(item.actualQuantity)
            : undefined,
          actualQuantityUnit: item.actualQuantityUnit ?? undefined,
          notes: item.notes ?? undefined,
        })),
        outputItems: (wo.outputItems ?? []).map((item) => ({
          id: item.id,
          itemId: item.itemId,
          itemName: item.item?.name,
          targetLotId: item.targetLotId ?? undefined,
          targetLotNumber: item.targetLot?.lotNumber,
          targetTagId: item.targetTagId ?? undefined,
          targetTagNumber: item.targetTag?.tagNumber,
          expectedQuantityValue: Number(item.expectedQuantity) || 0,
          expectedQuantityUnit: item.expectedQuantityUnit || 'Pounds',
          actualQuantityValue: item.actualQuantity
            ? Number(item.actualQuantity)
            : undefined,
          actualQuantityUnit: item.actualQuantityUnit ?? undefined,
          yieldPercentage: item.yieldPercentage
            ? Number(item.yieldPercentage)
            : undefined,
          notes: item.notes ?? undefined,
        })),
        createdAt: wo.timestamps.createdAt,
        updatedAt: wo.timestamps.updatedAt,
      };
    },
  },
);
